// src/features/analytics/AnalyticsUtils.ts

import { DateTime } from 'luxon';
import { ApiError } from '@/shared/api/ApiError';
import { ANALYTICS_LIVE_STALE_AFTER_SECONDS } from '@/lib/constants/analytics';
import {
  dayStartSeconds,
  nextDayStartSeconds,
  type DayZone,
  type UtcRange,
} from '@/shared/utils/utcRange';
import type { AnalyticsCoverage, AnalyticsFreshness } from './types';

const BYTE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'] as const;
// Binary base, matching the platform's existing byte convention
// (AccountUtils: 1 GB = 1024^3 bytes on /account/usage).
const BYTES_PER_UNIT = 1024;

export type AnalyticsErrorKind =
  | 'empty'           // no data for the range (range_outside_coverage / product window)
  | 'busy'            // transient overload — retry shortly
  | 'timeout'         // query took too long — narrow the range
  | 'rangeInvalid'    // bad range/selector (should be prevented by the controls)
  | 'notAvailable'    // scope errors — not available for this account
  | 'notAuthorized'   // platform VError 403
  | 'notEnabled'      // gateway flag off (plain 404, expected in local dev)
  | 'generic';

export interface AnalyticsErrorView {
  kind: AnalyticsErrorKind;
  title: string;
  detail?: string;
  availableFrom?: number;
  availableTo?: number;
  /** True for the pre-product-window case — the error state offers the Legacy Reports route. */
  legacyPointer?: boolean;
}

export interface CoverageNoticeView {
  message: string;
}

/**
 * Formatting and error-mapping helpers for the analytics feature.
 * Static class per repo convention.
 */
export class AnalyticsUtils {
  /** "4h 12m" style watch-time label from milliseconds. */
  static formatWatchTime(ms: number): string {
    if (!isFinite(ms) || ms <= 0) return '0m';
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m ${seconds}s`;
    return `${seconds}s`;
  }

  /** Byte label using the platform's binary (1024-based) convention. */
  static formatBytes(bytes: number): string {
    if (!isFinite(bytes) || bytes < 0) return '0 B';
    let value = bytes;
    let unitIndex = 0;
    while (value >= BYTES_PER_UNIT && unitIndex < BYTE_UNITS.length - 1) {
      value /= BYTES_PER_UNIT;
      unitIndex += 1;
    }
    const label = unitIndex === 0 ? String(Math.round(value)) : value.toFixed(1);
    return `${label} ${BYTE_UNITS[unitIndex]}`;
  }

  /** Locale-grouped integer count. */
  static formatCount(value: number): string {
    if (!isFinite(value)) return '0';
    return Math.round(value).toLocaleString();
  }

  /** Whole-percent label from a 0..1 fraction ("42%"). */
  static formatPercent(fraction: number): string {
    if (!isFinite(fraction)) return '0%';
    return `${Math.round(fraction * 100)}%`;
  }

  /** Suffix naming the day zone in labels: "UTC" or "local". */
  static zoneSuffix(zone: DayZone): string {
    return zone === 'utc' ? 'UTC' : 'local';
  }

  /**
   * Custom-range label for period-scoped headings ("Jun 1 – Jun 30, 2026,
   * UTC" / "…, local"). Ranges are end-exclusive calendar days in `zone`, so
   * the shown end day is the day before `to` (matching the range picker's
   * display).
   */
  static formatDayRangeLabel(range: UtcRange, zone: DayZone = 'utc'): string {
    const fmt = (seconds: number) => DateTime.fromSeconds(seconds, { zone }).toFormat('MMM d, yyyy');
    return `${fmt(range.from)} – ${fmt(range.to - 1)}, ${this.zoneSuffix(zone)}`;
  }

  /** The pre-ADR-009 name (UTC days). */
  static formatUtcDayRangeLabel(range: UtcRange): string {
    return this.formatDayRangeLabel(range, 'utc');
  }

  /**
   * Compact inclusive day span in `zone`: "Apr 23 – Jul 22, 2026" within one
   * year, "Nov 12, 2025 – Jan 5, 2026" across years, "Jul 22, 2026" for one day.
   */
  static formatDaySpan(firstDaySeconds: number, lastDaySeconds: number, zone: DayZone = 'utc'): string {
    const first = DateTime.fromSeconds(firstDaySeconds, { zone });
    const last = DateTime.fromSeconds(lastDaySeconds, { zone });
    if (first.hasSame(last, 'day')) return last.toFormat('MMM d, yyyy');
    if (first.year === last.year) {
      return `${first.toFormat('MMM d')} – ${last.toFormat('MMM d, yyyy')}`;
    }
    return `${first.toFormat('MMM d, yyyy')} – ${last.toFormat('MMM d, yyyy')}`;
  }

  /** The pre-ADR-009 name (UTC days). */
  static formatUtcDaySpan(firstDaySeconds: number, lastDaySeconds: number): string {
    return this.formatDaySpan(firstDaySeconds, lastDaySeconds, 'utc');
  }

  /**
   * True when coverage starts after the selected range's first day (in
   * `zone`) — the range reaches back before collection began (vs. a gap at
   * the end, where data merely lags). Gates the Legacy Reports pointer.
   */
  static coverageGapAtStart(coverage: AnalyticsCoverage, range: UtcRange, zone: DayZone = 'utc'): boolean {
    return dayStartSeconds(coverage.from, zone) > range.from;
  }

  /**
   * Decide the partial-coverage banner; null = show nothing. The ONLY case a
   * customer needs told about is history missing at the range START (days
   * before the account's data begins were genuinely unmeasured — the banner
   * routes eligible accounts to Legacy Reports). Trailing gaps are silent on
   * purpose: collection is always-on and daily materialization is same-day
   * (verified against prod 2026-07-23), so days the gateway omits at the end
   * are real zeros the chart already draws. That is why the covered span's
   * END is the selection end capped at TODAY — never `coverage.to` (the last
   * day with rows), which would re-surface a quiet tail as "data stops at X"
   * and invite the very question this banner exists to prevent.
   */
  /**
   * Where a requested window sits against stored data bounds (`to` is the
   * inclusive last covered instant, as the gateway sends it):
   *   'unknown'     — no bounds at all (nothing stored);
   *   'before-data' — the window ends at or before the first stored instant
   *                   (the head gap: "data is available from …" is the truth);
   *   'no-data-yet' — the window starts after the last stored instant (today,
   *                   or any window past the last data hour): EMPTY INSIDE
   *                   coverage, never "outside" it;
   *   'inside'      — the window overlaps the stored extent.
   * Since api ADR-0016 (live 2026-09-16) a 'no-data-yet' window arrives as a normal
   * 200 with an empty data block, so this state only steers copy and the coverage
   * notice (coverageIsEmpty), never error handling.
   */
  static windowDataState(
    range: { from: number; to: number },
    bounds?: { from: number; to: number } | null
  ): 'unknown' | 'before-data' | 'no-data-yet' | 'inside' {
    if (!bounds) return 'unknown';
    if (range.to <= bounds.from) return 'before-data';
    if (range.from > bounds.to) return 'no-data-yet';
    return 'inside';
  }

  /**
   * True when a response carries no coverage at all, says its dataset is
   * empty, or (given the range) predates the stored data — the cases
   * AnalyticsCoverageNotice explains with a gray alert. A range after the last
   * stored instant is NOT empty coverage (head-gap-only rule). When false and
   * the range sits inside coverage, the notice renders NOTHING, so a card must
   * supply its own "no data" line (never a blank chart box).
   */
  static coverageIsEmpty(
    coverage?: AnalyticsCoverage,
    freshness?: AnalyticsFreshness,
    range?: { from: number; to: number }
  ): boolean {
    if (!coverage) return true;
    const state = range ? this.windowDataState(range, coverage) : 'inside';
    if (state === 'before-data') return true;
    if (state === 'no-data-yet') return false;
    return freshness?.state === 'empty';
  }

  static coverageNoticeView(
    coverage: AnalyticsCoverage,
    range: UtcRange,
    nowSeconds: number,
    zone: DayZone = 'utc'
  ): CoverageNoticeView | null {
    if (!coverage.partial || !this.coverageGapAtStart(coverage, range, zone)) return null;
    const lastSelectedInstant = range.to - 1;
    const endOfToday = nextDayStartSeconds(nowSeconds, zone) - 1;
    const covered = this.formatDaySpan(
      Math.max(coverage.from, range.from),
      Math.min(lastSelectedInstant, endOfToday),
      zone
    );
    const selected = this.formatDaySpan(range.from, lastSelectedInstant, zone);
    const suffix = zone === 'utc' ? '(UTC)' : '(local time)';
    return { message: `Data covers ${covered} of your selected range ${selected} ${suffix}.` };
  }

  /** A live snapshot built more than ~3 minutes ago is stale. */
  static isLiveStale(builtAtSeconds: number, nowSeconds: number): boolean {
    return nowSeconds - builtAtSeconds > ANALYTICS_LIVE_STALE_AFTER_SECONDS;
  }

  /**
   * Map a query error to a renderable view state — structured `code` first,
   * HTTP status second (plan error-handling table).
   */
  static errorView(error: unknown): AnalyticsErrorView {
    if (!(error instanceof ApiError)) {
      return { kind: 'generic', title: 'Analytics could not be loaded.' };
    }

    switch (error.code) {
      case 'range_outside_coverage':
        return {
          kind: 'empty',
          title: 'No analytics data for this range.',
          availableFrom: error.availableFrom,
          availableTo: error.availableTo,
        };
      case 'range_before_product_window':
        return {
          kind: 'empty',
          title: 'New Analytics starts later than this range.',
          detail: 'Pick a range on or after the analytics start date.',
          availableFrom: error.availableFrom,
          legacyPointer: true,
        };
      case 'overloaded':
      case 'downstream_unavailable':
        return {
          kind: 'busy',
          title: 'Analytics is busy — retrying shortly.',
          detail: 'Try again in a moment.',
        };
      case 'query_timeout':
      case 'gateway_timeout':
        return {
          kind: 'timeout',
          title: 'This query took too long.',
          detail: 'Narrow the date range and retry.',
        };
      case 'range_too_large':
      case 'point_cap_exceeded':
      case 'invalid_time_range':
      case 'invalid_bucket':
      case 'invalid_dimension':
      case 'invalid_metric':
      case 'invalid_limit':
      case 'invalid_cid':
      case 'invalid_media_id':
        return {
          kind: 'rangeInvalid',
          title: 'This request could not be processed.',
          detail: 'Adjust the date range or selection and retry.',
        };
      case 'scope_inactive':
      case 'scope_not_found':
      case 'scope_removed':
      case 'not_top_level':
      case 'invalid_parent':
        return { kind: 'notAvailable', title: 'Not available for this account.' };
      default:
        break;
    }

    if (error.httpStatusCode === 403) {
      // Platform VError (not authenticated/authorized) — HTTP 403, no auto-logout.
      return { kind: 'notAuthorized', title: 'You are not authorized to view analytics.' };
    }
    if (error.httpStatusCode === 404) {
      // Gateway flag off (e.g. dev against the production proxy until enablement).
      return { kind: 'notEnabled', title: 'Analytics is not enabled in this environment.' };
    }
    return { kind: 'generic', title: 'Analytics could not be loaded.' };
  }
}
